import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { membersApi, settingsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Search, User, Mail, Phone, MapPin } from 'lucide-react';

const MemberDirectoryPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [membersData, fieldsData] = await Promise.all([
        membersApi.getAll(),
        settingsApi.getCustomFields().catch(() => [])
      ]);
      setMembers(membersData);
      setCustomFields(fieldsData);
    } catch (error) {
      console.error('Error loading members:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-r from-stone-800 to-stone-700 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Member Directory
          </h1>
          <p className="text-xl text-stone-300 max-w-2xl mx-auto">
            Connect with our church family
          </p>
        </div>
      </section>

      <section className="py-8 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4">
          {/* Search */}
          <div className="mb-8">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
              <Input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="member-search-input"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700 mx-auto"></div>
              <p className="mt-4 text-stone-600">Loading directory...</p>
            </div>
          ) : filteredMembers.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredMembers.map((member) => (
                <Card key={member.id} className="hover:shadow-lg transition-shadow" data-testid="member-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {member.photo_data || member.photo_url ? (
                          <img
                            src={member.photo_data || member.photo_url}
                            alt={member.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="h-8 w-8 text-stone-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-800">{member.name}</h3>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      {member.email && (
                        <a href={`mailto:${member.email}`} className="flex items-center text-stone-600 hover:text-amber-700">
                          <Mail className="h-4 w-4 mr-2 text-amber-700" />
                          {member.email}
                        </a>
                      )}
                      {member.phone && (
                        <a href={`tel:${member.phone}`} className="flex items-center text-stone-600 hover:text-amber-700">
                          <Phone className="h-4 w-4 mr-2 text-amber-700" />
                          {member.phone}
                        </a>
                      )}
                      {member.address && (
                        <div className="flex items-start text-stone-600">
                          <MapPin className="h-4 w-4 mr-2 text-amber-700 mt-0.5" />
                          {member.address}
                        </div>
                      )}
                      
                      {/* Custom Fields */}
                      {customFields.map((field) => {
                        const value = member.custom_fields?.[field.field_name];
                        if (!value) return null;
                        return (
                          <div key={field.field_name} className="text-stone-600">
                            <span className="text-stone-500">{field.field_name}:</span> {value}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8 text-center">
                <User className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-stone-800 mb-2">
                  {searchTerm ? 'No Members Found' : 'No Members Yet'}
                </h3>
                <p className="text-stone-600">
                  {searchTerm 
                    ? 'Try adjusting your search terms.'
                    : 'The member directory is being set up.'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default MemberDirectoryPage;
